import React, { useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';
import BillingReceipt from './BillingReceipt';
import axios from 'axios';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { BsInfoCircle as InfoCircle } from 'react-icons/bs';

const BillingSummary = ({
    title,
    amount,
    comparisonAmount,
    progressPercentage,
    discount,
    finalAmount,
    detail,
    selectedMonth, // string: "2025-05" 등
    onMonthChange,
    pricingPolicy,
    availableMonths = [],
    showProgress = false,
    isForecast = false,
    isSamePeriodComparison = false,
    decimalPlaces = 2,
    roundingMethod = 'round',
    isFinal = false,
    isFinalAmount
}) => {
    const [hasInterimSettlement, setHasInterimSettlement] = useState(false);
    // 60만원을 제외한 “조정된” 이번달 비용
    const adjustedAmount = amount - 600000;
    const percentageChange = comparisonAmount
        ? ((adjustedAmount - comparisonAmount) / comparisonAmount) * 100
        : 0;
    // 예측 비용의 경우, 60 만원 조정 필요 없음
    const forecastCostpercentageChange = comparisonAmount
        ? ((amount - comparisonAmount) / comparisonAmount) * 100
        : 0;

    const formatPercentage = (value) => {
        if (roundingMethod === "round") {
            return Number(Math.abs(value)).toFixed(decimalPlaces);
        } else if (roundingMethod === "floor") {
            const multiplier = Math.pow(10, decimalPlaces);
            return (Math.floor(Math.abs(value) * multiplier) / multiplier).toFixed(decimalPlaces);
        }
        return Number(Math.abs(value)).toFixed(decimalPlaces);
    };

    // 월 선택 드롭다운 노출 조건
    const isMonthSelector = isFinal && selectedMonth && onMonthChange;

    // 월 옵션이 string 배열이면 파싱
    const parsedMonths = availableMonths.map(opt => {
        if (typeof opt === 'string') {
            const parts = opt.split('_');
            const baseYearMonth = parts[0];
            const isFinal = parts.includes('final');
            // '2025-07-20T23:11:00.123456' 에서 날짜 부분만 추출
            const finalizedDate = isFinal && parts.length > 2 ? parts[2].split('T')[0] : null;
            const [year, month] = baseYearMonth.split('-').map(Number);

            return { year, month, value: opt, isFinal, finalizedDate };
        }
        return { ...opt, value: `${opt.year}-${String(opt.month).padStart(2, '0')}` };
    });

    // 월 선택 핸들러
    const handleMonthChange = (e) => {
        onMonthChange(e.target.value);
    };

    // 진행률 계산
    const getCurrentMonthProgress = () => {
        const now = new Date();
        let year, month;
        if (selectedMonth) {
            // _final이 있는 경우 제거하고 파싱
            const baseYearMonth = selectedMonth.replace('_final', '');
            [year, month] = baseYearMonth.split('-').map(Number);
        } else {
            year = now.getFullYear();
            month = now.getMonth() + 1;
        }
        const lastDay = new Date(year, month, 0).getDate();
        const isCurrentMonth = now.getFullYear() === year && (now.getMonth() + 1) === month;
        // 어제 날짜로 진행률 계산
        const yesterday = isCurrentMonth ? Math.max(now.getDate() - 1, 1) : lastDay;
        const effectiveDay = isCurrentMonth ? Math.min(yesterday, lastDay) : lastDay;
        return {
            day: effectiveDay,
            lastDay: lastDay,
            percentage: (effectiveDay / lastDay) * 100
        };
    };

    const monthProgress = getCurrentMonthProgress();
    const [showReceipt, setShowReceipt] = useState(false);

    return (
        <Card className="h-100">
            <Card.Header
                className={isMonthSelector ? "d-flex justify-content-between align-items-center" : ""}
                style={{ minHeight: '73px', padding: '12px 16px' }}
            >
                <h4 className="mb-0">{title}</h4>
            </Card.Header>
            <Card.Body className={isFinal ? "bg-body-tertiary" : (isForecast ? "d-flex flex-column justify-content-between text-center" : "")}>
                {isMonthSelector && (
                    <select
                        className="form-select mb-3"
                        value={selectedMonth}
                        onChange={handleMonthChange}
                    >
                        {parsedMonths.length > 0 ? (
                            parsedMonths.map(opt => {
                                // 현재 년월 확인 (KST 기준)
                                const now = new Date();
                                const kstNow = new Date(now.getTime() + (9 * 60 * 60 * 1000));
                                const currentYear = kstNow.getFullYear();
                                const currentMonth = kstNow.getMonth() + 1; // 0부터 시작하므로 +1

                                // 현재 달인지 확인
                                const isCurrentMonth = opt.year === currentYear && opt.month === currentMonth;

                                return (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.year}년 {opt.month}월{opt.isFinal ? ` 중간정산${opt.finalizedDate ? ` (${opt.finalizedDate})` : ''}` : (isCurrentMonth ? ' (누적)' : '')}
                                    </option>
                                );
                            })
                        ) : (
                            <option value={selectedMonth}>{selectedMonth}</option>
                        )}
                    </select>
                )}

                {pricingPolicy && isFinal && (
                    <p className="fs-10 text-muted mb-3">적용 요금 정책: {pricingPolicy}</p>
                )}

                {title !== "최종 요금" && !isForecast && (
                    <div>
                        <div className="d-flex align-items-center mb-2">
                            <h4 className="fs-6 fw-bold text-700 mb-0">₩{amount?.toLocaleString() || '0'}</h4>
                            <OverlayTrigger
                                placement="top"
                                delay={{ show: 150, hide: 300 }}
                                overlay={
                                    <Tooltip
                                        id="policy-info-tooltip"
                                        style={{
                                            '--bs-tooltip-bg': 'var(--falcon-primary)',
                                            '--bs-tooltip-color': '#fff',
                                            '--bs-tooltip-border-radius': '0.5rem',
                                            '--bs-tooltip-padding-x': '0.75rem',
                                            '--bs-tooltip-padding-y': '0.5rem',
                                            '--bs-tooltip-max-width': '280px',
                                            fontSize: '0.8125rem',
                                            boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)'
                                        }}
                                    >
                                        <div className="text-center">
                                            <div className="fw-semibold mb-1">💰 정책 서버 비용 포함</div>
                                            <div className="opacity-90" style={{ fontSize: '0.75rem', lineHeight: '1.4' }}>
                                                표시된 금액에 Policy 서버<br />기본 운영 비용이 포함되어 있습니다.
                                            </div>
                                        </div>
                                    </Tooltip>
                                }
                            >
                                <span className="d-inline-flex align-items-center">
                                    <InfoCircle
                                        className="ms-2 text-info"
                                        style={{
                                            cursor: 'pointer',
                                            fontSize: '0.875rem',
                                            filter: 'drop-shadow(0 0 0.125rem rgba(13, 110, 253, 0.25))'
                                        }}
                                    />
                                </span>
                            </OverlayTrigger>
                        </div>
                        {comparisonAmount !== undefined && (
                            <p className={`fs-7 fw-bold mb-2 ${percentageChange > 0 ? 'text-danger' : 'text-success'}`}>
                                {percentageChange > 0 ? '▲' : '▼'} {formatPercentage(percentageChange)}%
                                {isSamePeriodComparison && <span className="ms-1 fs-10">(지난달 동일 기간 대비)</span>}
                            </p>
                        )}
                    </div>
                )}

                {isForecast && (
                    <div className="d-flex flex-column align-items-center" style={{ gap: '0.5rem' }}>
                        <h4 className="fs-6 fw-bold text-700 mb-1">
                            ₩{amount?.toLocaleString() || '0'}
                            <span className="fw-normal text-600"> 예상</span>
                        </h4>
                        {comparisonAmount !== undefined && (
                            <p className={`fs-7 fw-bold mb-1 ${forecastCostpercentageChange >= 0 ? 'text-danger' : 'text-success'}`}>
                                {forecastCostpercentageChange >= 0 ? '▲' : '▼'} {formatPercentage(forecastCostpercentageChange)}%
                            </p>
                        )}
                        <p className="fs-10 fw-semi-bold mb-0">
                            지난달: ₩{comparisonAmount?.toLocaleString() || '0'}
                        </p>
                    </div>
                )}

                {progressPercentage !== undefined && showProgress && (
                    <div>
                        <p className="mb-0 text-center fs-11 mt-3 text-500">
                            {selectedMonth ? (() => {
                                const monthData = parsedMonths.find(m => m.value === selectedMonth);
                                const interimText = monthData?.isFinal ? ` 중간정산${monthData.finalizedDate ? ` (${monthData.finalizedDate})` : ''}` : '';
                                return `${monthData?.year}년 ${monthData?.month}월${interimText} `;
                            })() : '이번달 '}
                            <span className="text-800">{monthProgress.day}일</span> / {monthProgress.lastDay}일
                        </p>
                        <div className="progress" style={{ height: "35px", marginTop: "8px" }}>
                            <div
                                className="progress-bar"
                                role="progressbar"
                                style={{
                                    width: `${monthProgress.percentage}%`,
                                    transition: "width 0.5s ease-in-out"
                                }}
                                aria-valuenow={monthProgress.percentage}
                                aria-valuemin="0"
                                aria-valuemax="100"
                            >
                                <span className="fs-8 fw-bold">{monthProgress.day}일</span>
                            </div>
                        </div>
                    </div>
                )}

                {isFinal && (
                    <>
                        <div className="d-flex justify-content-between fs-10 mb-1">
                            <p className="mb-0">총 금액</p>
                            <span>₩{amount?.toLocaleString() || '0'}</span>
                        </div>
                        <div className="d-flex justify-content-between fs-10 mb-1 text-success">
                            <p className="mb-0">구간 할인</p>
                            <span>-₩{discount?.toLocaleString() || '0'}</span>
                        </div>
                        <hr />
                        <h5 className="d-flex justify-content-between">
                            <span>최종 금액</span>
                            <span>₩{finalAmount?.toLocaleString() || '0'}</span>
                        </h5>
                        {/* 중간 정산 버튼: 현재 달이고 + 달이 끝나지 않았고 + 오늘 중간정산을 안했다면 노출 */}
                        {(() => {
                            // 현재 달이 끝나지 않았는지 확인 (KST 기준)
                            // 서버에서 넘어오는 데이터가 KST 기준이므로 클라이언트도 KST 기준으로 계산
                            const now = new Date();

                            // KST 시간을 정확하게 계산 (Asia/Seoul 시간대 사용)
                            const kstFormatter = new Intl.DateTimeFormat('ko-KR', {
                                timeZone: 'Asia/Seoul',
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit'
                            });

                            const kstDateParts = kstFormatter.formatToParts(now);
                            const currentYear = parseInt(kstDateParts.find(part => part.type === 'year').value);
                            const currentMonth = parseInt(kstDateParts.find(part => part.type === 'month').value) - 1; // 0부터 시작하도록 조정
                            const currentDay = parseInt(kstDateParts.find(part => part.type === 'day').value);

                            // 선택된 월이 현재 달인지 확인
                            const currentYearMonth = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`;
                            const selectedYearMonth = selectedMonth?.replace('_final', '') || currentYearMonth;
                            const isCurrentMonth = selectedYearMonth === currentYearMonth;

                            if (!isCurrentMonth) {
                                return false;
                            }

                            // 현재 월의 마지막 날
                            const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

                            // 현재 날짜가 월의 마지막 날이 아니라면 버튼 표시
                            const isNotEndOfMonth = currentDay < lastDayOfMonth;

                            // 오늘 이미 중간정산했는지 확인 (KST 기준으로 서버 데이터와 매칭)
                            const todayKST = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(currentDay).padStart(2, '0')}`;

                            // 디버깅용 로그
                            console.log('중간정산 버튼 디버깅:', {
                                todayKST,
                                availableMonths,
                                selectedYearMonth,
                                currentYearMonth,
                                isCurrentMonth
                            });

                            const hasInterimToday = availableMonths.some(opt => {
                                if (typeof opt !== 'string') return false;
                                const parts = opt.split('_');
                                const result = parts.length === 3 && parts[1] === 'final' && parts[2].startsWith(todayKST);

                                if (parts[1] === 'final') {
                                    console.log('중간정산 항목 발견:', {
                                        opt,
                                        parts,
                                        todayKST,
                                        startsWithToday: parts[2].startsWith(todayKST),
                                        result
                                    });
                                }

                                return result;
                            });

                            const shouldShowButton = isNotEndOfMonth && !hasInterimToday && !hasInterimSettlement;

                            console.log('중간정산 버튼 표시 여부:', {
                                isNotEndOfMonth,
                                hasInterimToday,
                                hasInterimSettlement,
                                shouldShowButton
                            });

                            return shouldShowButton;
                        })() && (
                                <button
                                    className="btn btn-info w-100 mt-2"
                                    onClick={async () => {
                                        // KST 기준 날짜 계산 (서버와 동일한 시간대 사용)
                                        const now = new Date();
                                        const kstNow = new Date(now.getTime() + (9 * 60 * 60 * 1000));

                                        // 어제까지 중간정산 (KST 기준)
                                        const kstYesterday = new Date(kstNow.getTime() - 24 * 60 * 60 * 1000);

                                        const year = kstNow.getFullYear();
                                        const month = String(kstNow.getMonth() + 1).padStart(2, '0');
                                        const start_date = `${year}-${month}-01`;
                                        const end_date = `${kstYesterday.getFullYear()}-${String(kstYesterday.getMonth() + 1).padStart(2, '0')}-${String(kstYesterday.getDate()).padStart(2, '0')}`;

                                        try {
                                            const { getConfig } = await import('../../../../config/getConfig');
                                            const config = getConfig();
                                            const apiBaseUrl = config.API_URL;

                                            // 토큰 설정
                                            const headers = { 'Content-Type': 'application/json' };
                                            const siteToken = sessionStorage.getItem('siteToken');
                                            const token = sessionStorage.getItem('token');

                                            if (siteToken) {
                                                headers.Authorization = `Bearer ${siteToken}`;
                                            } else if (token) {
                                                headers.Authorization = `Bearer ${token}`;
                                            }

                                            const res = await axios.post(
                                                `${apiBaseUrl}/billing/settlement?startDate=${start_date}&endDate=${end_date}&isFinalized=true`,
                                                {},  // 빈 body (파라미터는 쿼리스트링으로)
                                                { headers }
                                            );

                                            if (res.status === 200 && res.data?.success && res.data?.report) {
                                                setHasInterimSettlement(true);
                                                alert('중간 정산이 완료되었습니다.');
                                                window.location.reload(); // 페이지 새로고침
                                                // 필요시 res.data.report 활용
                                            } else {
                                                alert('중간 정산 요청에 실패했습니다.');
                                            }
                                        } catch (err) {
                                            alert('중간 정산 요청에 실패했습니다.');
                                        }
                                    }}
                                >
                                    중간 정산
                                </button>
                            )}
                        {(isFinalAmount || hasInterimSettlement) && (
                            <button
                                className="btn btn-sm btn-outline-primary w-100 mt-2"
                                onClick={() => setShowReceipt(true)}
                            >
                                <i className="fas fa-receipt me-1"></i> 상세 내역 보기
                            </button>
                        )}
                    </>
                )}

                {showReceipt && (
                    <BillingReceipt
                        show={showReceipt}
                        onHide={() => setShowReceipt(false)}
                        detail={detail}
                        yearMonth={selectedMonth}
                        isFinalAmount={!!detail?.finalAmount}
                    />
                )}
            </Card.Body>
        </Card>
    );
};

export default BillingSummary;